import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { CheckIcon } from '@/atoms/Icons/CheckIcon'
import { LoadingSpinnerIcon } from '@/atoms/Icons/LoadingSpinnerIcon'
import { PlusIcon } from '@/atoms/Icons/PlusIcon'
import { TitleXXS } from '@/atoms/Text'
import { createWebClient } from '@/services/ApolloClient'
import { useUser } from '@/services/UserService'
import { useEditWatchlist, useIsTitleInWatchlist } from '@/services/WatchlistService'
import { useLocale } from '@/utils/LocaleUtil'
import { useSafeTrack } from '@/utils/analytics'
import { logger } from '@/utils/logging'
import { useTranslate } from '@/utils/translate/translate-client'
import { useToast } from '../Toast'
import { WatchlistTooltip } from './WatchlistTooltip'
import { DRY_BAR_SERIES_UUID } from './constants'

interface WatchlistButtonProps {
  titleId: string
}

export const WatchlistButton: React.FC<WatchlistButtonProps> = ({ titleId }) => {
  const [showSpinner, setShowSpinner] = useState(false)
  const { t } = useTranslate('common')
  const track = useSafeTrack()
  const { locale } = useLocale()
  const { userId } = useUser()
  const { showToast } = useToast()
  const spinnerTimeout = useRef<NodeJS.Timeout | null>(null)
  // TODO: when web implements user profiles, use that profile id here instead.
  const viewerProfileId = userId ?? undefined

  const client = useMemo(() => {
    return createWebClient({ locale, viewerProfileId })
  }, [locale, viewerProfileId])

  const { isInWatchlist, refetch } = useIsTitleInWatchlist({ titleId, client, viewerProfileId })
  const { addToWatchlist, deleteFromWatchlist, addToListLoading, deleteFromListLoading } = useEditWatchlist({ client })

  const handleButtonClick = useCallback(async () => {
    if (isInWatchlist) {
      try {
        await deleteFromWatchlist(titleId)
        await refetch()
        showToast(t('removedFromWatchlist', 'Removed from Watchlist'))
        track('Watchlist Event', { contentId: titleId, action: 'remove_item' })
      } catch (error) {
        showToast(
          t(
            'failedToUpdateFromWatchlist',
            'We ran into an issue while updating your watchlist. Please try again later.',
          ),
        )
        logger().error('Failed to remove from watchlist', { contentId: titleId }, error)
      }
    } else {
      try {
        await addToWatchlist(titleId)
        await refetch()
        showToast(t('addedToWatchlist', 'Added to Watchlist'))
        track('Watchlist Event', { contentId: titleId, action: 'add_item' })
      } catch (error) {
        showToast(
          t(
            'failedToUpdateFromWatchlist',
            'We ran into an issue while updating your watchlist. Please try again later.',
          ),
        )
        logger().error('Failed to add to watchlist', { contentId: titleId }, error)
      }
    }
  }, [isInWatchlist, deleteFromWatchlist, titleId, refetch, showToast, t, track, addToWatchlist])

  useEffect(() => {
    if (addToListLoading || deleteFromListLoading) {
      setShowSpinner(true)
      if (spinnerTimeout.current) clearTimeout(spinnerTimeout.current)
    } else if (showSpinner) {
      spinnerTimeout.current = setTimeout(() => setShowSpinner(false), 200)
    }
    return () => {
      if (spinnerTimeout.current) clearTimeout(spinnerTimeout.current)
    }
  }, [addToListLoading, deleteFromListLoading, showSpinner])

  const iconToShow = useMemo(() => {
    if (showSpinner) {
      return <LoadingSpinnerIcon color="white" size={24} />
    }
    return isInWatchlist ? <CheckIcon color="white" size={24} /> : <PlusIcon color="white" size={24} />
  }, [isInWatchlist, showSpinner])

  const tooltipText = useMemo(() => {
    if (showSpinner) {
      return t('updatingDot', 'Updating...')
    }
    return isInWatchlist ? t('removeFromWatchlist', 'Remove from Watchlist') : t('addToWatchlist', 'Add to Watchlist')
  }, [isInWatchlist, showSpinner, t])

  if (titleId === DRY_BAR_SERIES_UUID) return null

  const trigger = (
    <div onClick={handleButtonClick} className="w-full" role="button" tabIndex={0} aria-label={tooltipText}>
      <div className="hidden h-12 w-12 cursor-pointer items-center justify-center rounded-full bg-[#ffffff33] p-3 backdrop-blur-2xl transition-colors duration-200 focus:outline-none hover:bg-[#ffffff11] md:flex">
        {iconToShow}
      </div>
      <div className="mb-4 flex h-12 w-full items-center justify-center gap-[6px] rounded-xl bg-[#ffffff33] px-6 py-[14px] backdrop-blur-2xl transition-colors duration-200 focus:outline-none hover:bg-[#ffffff11] md:hidden">
        {iconToShow}
        {tooltipText}
      </div>
    </div>
  )

  const content = (
    <TitleXXS weight="bold" color="core-gray-950" className="">
      {tooltipText}
    </TitleXXS>
  )

  return (
    <WatchlistTooltip
      className="!w-full md:!w-fit"
      buttonClassName="!w-full md:!w-fit"
      panelClassName="!w-full md:!w-fit !min-w-[220px] text-center !px-2 !py-4 !rounded-lg"
      panelPosition="CENTER"
      isIconOnly={false}
      removeBorder={true}
      buttonStyle={{ padding: 0 }}
      trigger={trigger}
      content={content}
    />
  )
}
